import * as amplitudeClient from "@amplitude/analytics-browser"
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { AnalyticsInterface } from './types';
import { plugin as engagementPlugin} from '@amplitude/engagement-browser';
import getUserIdFromOkta from '../../utils/getUserIdFromOkta';

// Guides & Surveys boots when its plugin is added; added once per page, after identify.
let engagementAdded = false;

const createAnalytics = (apiKey: string, sessionReplay: boolean, 
          sampleRate: number = 1.0, 
          userId?: string) => {
  if (apiKey && apiKey !== '') {
    amplitudeClient.init(apiKey, userId, {
      defaultTracking: {
        sessions:true,
        pageViews:false,
        formInteractions:false,
        fileDownloads:false
      }
    });
    // The caller's userId can still be empty on first render (v2 reads it from Okta authState);
    // the stored token already has it.
    const currentUserId = userId || getUserIdFromOkta();
    if (currentUserId) {
      amplitudeClient.setUserId(currentUserId);
    }

    if(sessionReplay){
      const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: sampleRate,
        });
      amplitudeClient.add(sessionReplayTracking);
    }
  } else {
    console.error("API key is required to initialize Amplitude");
  }

  const track = (event: string, data?: any): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      try {
        amplitudeClient.track(event, data);
        resolve('success')
      } catch (e) {
        reject(new Error("something went wrong try again later"));
      }
    })
  };

  const setLanguage = async (lang: string): Promise<any> => {
      try {
        await window.engagement?.updateLanguage(lang);
      } catch (e) {
        throw e;
      }
  };

  const setTheme = (theme: string) => {
    try {
      window.engagement?.setThemeMode(theme);
    } catch (e) {
      throw e;
    }
  };

  const setUserId = (userId: string): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      try {
        amplitudeClient.setUserId(userId);
        resolve('success')
      } catch (e) {
        reject(new Error("something went wrong try again later"));
      }
    })
  };

  // Boots Guides & Surveys ("Engagement Booted"). Deferred from init so it boots with the
  // identified user's properties; resolves once the plugin's setup has finished.
  const bootEngagement = async (): Promise<void> => {
    if (engagementAdded || !apiKey) return;
    engagementAdded = true;
    await amplitudeClient.add(engagementPlugin()).promise;
  };

  const identify = (userProps: Record<string, any>): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      try {
        const identifyObj = new amplitudeClient.Identify();
        Object.keys(userProps).forEach((key => identifyObj.set(key, userProps[key])));
        amplitudeClient.identify(identifyObj);
        resolve('success');
      } catch (e) {
        reject(new Error("something went wrong try again later"))
      }
    })
  };

  const analytics: AnalyticsInterface = {
    track,
    setUserId,
    bootEngagement,
    identify,
    setTheme,
    setLanguage
  };

  return analytics;
}

// Resolves once UnifyContainer has sent the startup events ($identify -> Engagement Booted ->
// Log in Unify ...). Events that must not land in the middle of that sequence wait on it.
let resolveStartupTracked: () => void = () => { };
const startupTracked = new Promise<void>((resolve) => {
  resolveStartupTracked = resolve;
});
const markStartupTracked = () => resolveStartupTracked();
const whenStartupTracked = () => startupTracked;

export { createAnalytics, markStartupTracked, whenStartupTracked }
